import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2075&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
`;

const Subtitle = styled.p`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  font-weight: 500;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 3rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #004744;
  text-align: center;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ServiceDescription = styled.p`
  color: #e0e0e0;
  font-size: 0.95rem;
  line-height: 1.7;
  margin-top: 0.5rem;
`;

const ServiceLink = styled(Link)`
  text-decoration: none;
  display: block;
  width: 100%;
  height: 100%;
`;

const PropertyOwner: React.FC = () => {
  const { language } = useLanguage();
  
  const services = [
    { 
      id: 'gestione', 
      name: 'GESTIONE', 
      link: '/services/management',
      description: language === 'it' 
        ? 'Gestiamo completamente la tua proprietà, dalle prenotazioni alla pulizia, permettendoti di guadagnare senza preoccupazioni.'
        : 'We completely manage your property, from bookings to cleaning, allowing you to earn without worries.'
    },
    { 
      id: 'arredamento', 
      name: 'ARREDAMENTO', 
      link: '/services/furniture',
      description: language === 'it' 
        ? 'Servizi di arredamento professionale e home staging per massimizzare l\'attrattiva e il valore della tua proprietà.'
        : 'Professional furnishing and home staging services to maximize the appeal and value of your property.'
    },
    { 
      id: 'lavanderia', 
      name: 'LAVANDERIA', 
      link: '/services/laundry',
      description: language === 'it' 
        ? 'Servizio completo di lavanderia e gestione della biancheria per mantenere sempre impeccabile la tua proprietà.'
        : 'Complete laundry and linen management service to keep your property impeccable at all times.'
    },
    { 
      id: 'affiliazione', 
      name: 'AFFILIAZIONE', 
      link: '/services/affiliation',
      description: language === 'it' 
        ? 'Entra nel nostro network di affiliati e accedi a risorse esclusive, formazione e maggiore visibilità per la tua proprietà.'
        : 'Join our network of affiliates and access exclusive resources, training, and greater visibility for your property.'
    },
    { 
      id: 'popup', 
      name: 'POP UP', 
      link: '/services/pop-up',
      description: language === 'it' 
        ? 'Trasforma temporaneamente il tuo spazio in un\'esperienza unica per eventi, showroom o concept store.'
        : 'Temporarily transform your space into a unique experience for events, showrooms or concept stores.'
    },
    { 
      id: 'manutenzione', 
      name: 'MANUTENZIONE', 
      link: '/services/maintenance',
      description: language === 'it' 
        ? 'Servizi di manutenzione ordinaria e straordinaria per mantenere la tua proprietà in perfette condizioni.'
        : 'Regular and extraordinary maintenance services to keep your property in perfect condition.'
    },
    { 
      id: 'formazione', 
      name: 'FORMAZIONE', 
      link: '/services/training',
      description: language === 'it' 
        ? 'Corsi e programmi di formazione per proprietari che vogliono gestire autonomamente il proprio immobile.'
        : 'Courses and training programs for owners who want to independently manage their property.'
    },
    { 
      id: 'sicurezza', 
      name: 'SICUREZZA', 
      link: '/services/security',
      description: language === 'it' 
        ? 'Soluzioni per garantire la sicurezza della tua proprietà e degli ospiti durante il soggiorno.'
        : 'Solutions to ensure the safety of your property and guests during their stay.'
    },
    { 
      id: 'assicurazione', 
      name: 'ASSICURAZIONE', 
      link: '/services/insurance',
      description: language === 'it' 
        ? 'Polizze assicurative specializzate per proteggere il tuo immobile e la tua attività di affitto.'
        : 'Specialized insurance policies to protect your property and your rental business.'
    },
  ];

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const buttonVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    hover: { 
      scale: 1.05,
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
      y: -10
    },
    tap: { scale: 0.95 }
  };
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Servizi per Proprietari | J-O-H' : 'Services for Property Owners | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri i nostri servizi per proprietari di immobili che desiderano dare in affitto la propria casa' 
            : 'Discover our services for property owners who want to rent out their home'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'Vuoi dare in affitto una casa?' : 'Want to rent out a house?'}</PageTitle>
          <Subtitle>{language === 'it' ? 'ECCO COSA POSSIAMO FARE PER TE!' : 'HERE IS WHAT WE CAN DO FOR YOU!'}</Subtitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ServicesGrid>
          {services.map((service, index) => (
            service.link ? (
              <ServiceLink key={service.id} to={service.link}>
                <ServiceCard
                  variants={buttonVariants}
                  initial="initial"
                  animate="animate"
                  whileHover="hover"
                  whileTap="tap"
                  transition={{ duration: 0.2, delay: index * 0.1 }}
                >
                  <ServiceTitle>{service.name}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                </ServiceCard>
              </ServiceLink>
            ) : (
              <ServiceCard
                key={service.id}
                variants={buttonVariants}
                initial="initial"
                animate="animate"
                whileHover="hover"
                whileTap="tap"
                transition={{ duration: 0.2, delay: index * 0.1 }}
              >
                <ServiceTitle>{service.name}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            )
          ))}
        </ServicesGrid>
      </ContentSection>
    </PageContainer>
  );
};

export default PropertyOwner; 